import { Grid, Stack, SxProps, Theme, Typography } from '@mui/material';
import dayjs from 'dayjs';
import { RollupType, toRollupType } from 'pages/raas/helpers/labels';
import { getRpcCountMetricPanel } from 'pages/raas/helpers/rollup';
import { useRaasDeployment, useRaasMetrics, useRpcInstances } from 'pages/raas/hooks';
import React, { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { Section } from 'shared/components';
import { DateRangePicker } from 'shared/components/Date';
import { commify } from 'shared/utils/strings';

import { useTimeFilterOptions } from '../../hooks/useTimeFilterOptions';
import { DateFilterVariant } from '../../store';
import RpcCountMetric from './RpcCountMetric';
import { IRpcMonitoringSections } from './RpcUsageMonitoringView';

enum RpcCountVariant {
  INFO = 'INFO',
  WARNING = 'WARNING',
}
interface IRpcCountCard {
  title: string;
  value: string | number;
  variant?: RpcCountVariant;
  sx?: SxProps<Theme>;
}

const RpcCountSection: React.FC<IRpcMonitoringSections> = ({ instance }) => {
  const { rollupId } = useParams();
  const { data: rollup } = useRaasDeployment();

  const { end, step, timeframe } = useTimeFilterOptions('month');

  const { data: rpcInstancesData } = useRpcInstances();

  console.log('RPC INSTANCE', rpcInstancesData);

  const { data: result } = useRaasMetrics({
    panel: getRpcCountMetricPanel(toRollupType(rollup?.type)),
    timeframe,
    rollupId,
    start: dayjs().startOf('month').format('YYYY-MM-DD'),
    end,
    step,
    rpcInstance: instance,
  });

  const rpcCountToday = useMemo(() => {
    const todayCount = result?.data.find(
      data => dayjs(data.date).format('DD/MM/YYYY') === dayjs().format('DD/MM/YYYY'),
    );

    return commify(todayCount?.['daily rpc count'] ?? 0);
  }, [result]);

  const rpcCountMonth = useMemo(() => {
    return result?.data.reduce((acc, data) => acc + (Number(data?.['daily rpc count']) ?? 0), 0);
  }, [result]);

  return (
    <Grid container>
      <Grid
        item
        sx={{ borderRight: theme => `1px solid ${theme.colors.functional.subject.border}` }}
        xs={8}
      >
        <Section
          renderSubHeaderEndContent={
            <DateRangePicker variant={DateFilterVariant.DAILY_RPC_COUNT_SECTION} />
          }
          title="RPC Requests (Daily)"
        >
          <Grid container>
            <Grid item xs={12}>
              <RpcCountMetric instance={instance} />
            </Grid>
          </Grid>
        </Section>
      </Grid>

      <Grid item xs={4}>
        <Stack height="100%" sx={{ backgroundColor: '#FFFFFF' }}>
          <RpcCountCard
            sx={{ borderBottom: theme => `1px solid ${theme.colors.functional.subject.border}` }}
            title="RPC Requests (Current Day)"
            value={rpcCountToday}
          />

          <RpcCountCard
            sx={{ borderBottom: theme => `1px solid ${theme.colors.functional.subject.border}` }}
            title="Total RPC Requests (Current month)"
            value={commify(rpcCountMonth ?? 0)}
            variant={
              (rpcCountMonth ?? 0) > (rpcInstancesData?.monthlyRpcLimit ?? 0) &&
              rollup?.type === RollupType.ORBIT
                ? RpcCountVariant.WARNING
                : RpcCountVariant.INFO
            }
          />

          {rollup?.type === RollupType.ORBIT && (
            <RpcCountCard
              title="Total RPC Requests Limit (Current month)"
              value={commify(rpcInstancesData?.monthlyRpcLimit ?? 0)}
            />
          )}
        </Stack>
      </Grid>
    </Grid>
  );
};

const RpcCountCard: React.FC<IRpcCountCard> = ({
  sx,
  title,
  value,
  variant = RpcCountVariant.INFO,
}) => {
  console.log('TITLE', title);
  console.log('VARIANT', variant);

  return (
    <Stack alignItems="center" direction="row" height="100%" sx={{ p: '40px', ...sx }}>
      <Stack spacing="20px">
        <Typography variant="captionC">{title}</Typography>
        <Typography
          fontSize="40px"
          sx={{
            color: theme =>
              variant === RpcCountVariant.WARNING
                ? theme.colors.schema.warning
                : theme.colors.functional.text.primary,
          }}
          variant="h4"
        >
          {value}
        </Typography>
      </Stack>
    </Stack>
  );
};

export default RpcCountSection;
